import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useDropzone } from 'react-dropzone'
import { read, utils } from 'xlsx'
import UploadImage from '../../images/uploadfile.png'
import Layout from 'components/Layout'
import Spinner from 'components/Spinner'
import { createBulkLocations } from 'slices/bulkLocationSlice'
import geocodeAddress from 'functions/geocodeAddress'

const MyDropzone = ({ apiKey }: { apiKey: string }) => {
	const dispatch = useDispatch()
	const [dropped, setDropped] = useState(false)
	const [working, setWorking] = useState(false)

	interface LocationData {
		assetName: string
		city: string
		stateProvince: string
		postalCode: string
		[key: string]: any
	}

	const [bulkLocations, setBulkLocations] = useState<LocationData[]>([])

	const onDrop = useCallback((acceptedFiles: File[]) => {
		setWorking(true)
		acceptedFiles.forEach(async (file: File) => {
			if (file) {
				setDropped(true)
				const data = await file.arrayBuffer()
				const workbook = read(data)
				const workSheet = workbook.Sheets[workbook.SheetNames[0]]
				const sheetData = utils.sheet_to_json<LocationData>(workSheet)

				setTimeout(() => {
					setBulkLocations(sheetData)
				}, 500)
			}
		})
	}, [])
	const { getRootProps, getInputProps } = useDropzone({ onDrop })

	useEffect(() => {
		const buildGeocodeQuery = (location: any): string => {
			// Prefer a real street-address query (address + city + state + zip).
			// Nominatim, unlike Google Places, can't resolve queries that consist
			// only of a business/asset name like "Pet Supermarket 9". Fall back to
			// assetName + city/state/zip only if no street address is present.
			const trim = (s: any) => (s == null ? '' : String(s).trim())
			const street = trim(location.address)
			const city = trim(location.city)
			const state = trim(location.stateProvince)
			const zip = trim(location.postalCode)
			const assetName = trim(location.assetName)

			if (street) {
				return [street, city, state, zip].filter(Boolean).join(', ')
			}
			return [assetName, city, state, zip].filter(Boolean).join(', ')
		}

		const fetchNewLocations = async () => {
			return Promise.all(
				bulkLocations.map(async (location: any) => {
					const query = buildGeocodeQuery(location)

					if (!query) {
						console.warn('Bulk-load: skipping geocode for row with no usable address fields', location)
						return location
					}

					try {
						const data = await geocodeAddress(query, apiKey)
						return {
							...location,
							latitude: data.lat,
							longitude: data.lng,
						}
					} catch (error) {
						// Leave latitude/longitude unset on failure rather than the legacy 0/0 magic
						// fallback (0/0 is a real coordinate and falsy-checks falsely as "missing").
						// The row appears in the table without a map pill and can be re-geocoded
						// from the table UI via the row-level retry button.
						console.warn(`Bulk-load: geocoding failed for "${query}"`, error)
						return location
					}
				})
			)
		}

		const getNewLocations = async () => {
			if (bulkLocations.length > 0) {
				const newBulkLocations = await fetchNewLocations()

				if (newBulkLocations.length > 0) {
					dispatch(createBulkLocations(newBulkLocations))
				}
			}
		}

		getNewLocations()
	}, [apiKey, dispatch, bulkLocations])

	return (
		<Layout>
			<Layout.Body>
				<div>
					{working ? (
						<div className="w-full">
							<div className="w-full h-1 bg-gray-200 rounded overflow-hidden">
								<div className="h-full bg-blue-500 animate-pulse w-full" />
							</div>
							<p>File loading. . .</p>
						</div>
					) : (
						<div
							{...getRootProps()}
							className="bg-[#b9edf8] w-full h-[60vh] flex items-center justify-center flex-col border-2 border-dashed border-[#25a4fc] rounded-[15px] cursor-pointer"
						>
							<input {...getInputProps()} />
							<img className="w-[200px] h-[200px] border-none" src={UploadImage} alt="Upload" />
							<h2 className="text-[1.75rem] font-normal text-[#25a4fc] pt-5 pb-2">Bulk Load Locations</h2>
							<p className="text-sm text-gray-600 max-w-md text-center leading-relaxed">
								Upload an Excel or CSV file containing your locations. You can:
							</p>
							<ul className="mt-2 text-sm text-gray-600 space-y-1">
								<li className="flex items-center gap-2">
									<span className="text-[#25a4fc] font-bold">&bull;</span>
									<span>
										<strong>Click</strong> anywhere in this area to open a file picker
									</span>
								</li>
								<li className="flex items-center gap-2">
									<span className="text-[#25a4fc] font-bold">&bull;</span>
									<span>
										<strong>Drag and drop</strong> a file directly onto this area
									</span>
								</li>
							</ul>
						</div>
					)}
				</div>
			</Layout.Body>
		</Layout>
	)
}

export default MyDropzone
